import { FormCapsLabel } from '@/components/ui/form-caps-label'
import { FormFieldLabel } from '@/components/ui/form-field-label'
import { UnderlineInput } from '@/components/ui/underline-input'
import { loginTitle } from '@/lib/branding'

export interface LoginFormValues {
  email: string
  password: string
}

export interface LoginScreenProps {
  onSubmit?: (values: LoginFormValues) => void
  isSubmitting?: boolean
  errorMessage?: string | null
  /** Shop name for the default title ("Log in to <shop>"); `title` overrides it. */
  brandName?: string
  title?: string
  subtitle?: string
  kioskShortcutHref?: string
  kioskShortcutLabel?: string
}

export function LoginScreen(props: LoginScreenProps) {
  const {
    onSubmit,
    isSubmitting,
    errorMessage,
    brandName,
    title = loginTitle(brandName ?? ''),
    subtitle = 'Use your work email and password to access callouts, bookings and customers.',
    kioskShortcutHref,
    kioskShortcutLabel = 'Open Front Desk Kiosk',
  } = props

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (
    event,
  ) => {
    event.preventDefault()
    if (!onSubmit) return

    const formData = new FormData(event.currentTarget)
    const values: LoginFormValues = {
      email: String(formData.get('email') ?? ''),
      password: String(formData.get('password') ?? ''),
    }

    onSubmit(values)
  }

  return (
    <div className="bg-slate-950 text-slate-50">
      <main className="mx-auto flex max-w-md flex-col gap-6 px-4 py-12">
        <header className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">
            {title}
          </h1>
          <p className="text-sm text-slate-300">
            {subtitle}
          </p>
        </header>

        <form
          onSubmit={handleSubmit}
          className="space-y-6 rounded-xl bg-slate-900 p-6 shadow-xl ring-1 ring-slate-800"
        >
          <div className="space-y-4 text-xs">
            <FormCapsLabel className="text-slate-300">
              Sign in
            </FormCapsLabel>

            <div className="space-y-3">
              <div className="flex items-end gap-3">
                <FormFieldLabel className="min-w-[5.5rem] text-slate-200">
                  Email
                </FormFieldLabel>
                <UnderlineInput
                  name="email"
                  type="email"
                  className="w-auto flex-1 text-slate-50"
                  placeholder="you@example.com"
                  autoComplete="email"
                />
              </div>

              <div className="flex items-end gap-3">
                <FormFieldLabel className="min-w-[5.5rem] text-slate-200">
                  Password
                </FormFieldLabel>
                <UnderlineInput
                  name="password"
                  type="password"
                  className="w-auto flex-1 text-slate-50"
                  placeholder="Your password"
                  autoComplete="current-password"
                />
              </div>
            </div>

            {errorMessage ? (
              <p className="pt-1 text-xs text-red-400">
                {errorMessage}
              </p>
            ) : null}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="flex w-full items-center justify-center rounded-md bg-blue-500 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSubmitting ? 'Signing in…' : 'Log in'}
          </button>

          {kioskShortcutHref ? (
            <a
              href={kioskShortcutHref}
              className="flex w-full items-center justify-center rounded-md border border-slate-700 bg-slate-950 px-4 py-2 text-sm font-medium text-slate-100 shadow hover:bg-slate-800"
            >
              {kioskShortcutLabel}
            </a>
          ) : null}

          <p className="text-[11px] leading-snug text-slate-400">
            Accounts are created in the admin panel. If you don't have
            access yet, request it from your manager so your account can
            be added.
          </p>
        </form>
      </main>
    </div>
  )
}
